import { ChangeEvent, CSSProperties, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { centerClient } from '../../shared/clients';
import { UserCommandBaseRequest, LoginDevices as LoginDevicesDto, UserCommandType, UserCommandStatus, PerformUserCommandResponse } from '../../shared/generated';
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react';
import { ColDef, RowClassParams } from 'ag-grid-community';
import { AG_GRID_LOCALE_RU } from 'blick.react.aggrid';
import { Button, ButtonGroup, Col, Form, Modal, Row } from 'react-bootstrap';
import context from '../Administration/context/shared';
import { clearIntervalAsync, setIntervalAsync } from 'set-interval-async';
import { toast } from 'react-toastify';
import { InputPicker } from 'rsuite';
import { useIsMobile } from '../shared';
import { agTextColumnFilterProps, BlickAgGrid, SelectFilter } from 'blick.react.aggrid';
import { axiosWithAuth } from 'blick.react.utils';
import { buildDeviceId } from '../../utils/deviceId';

const getUserCommandStatusDescription = (status: UserCommandStatus | undefined) => {
    switch (status) {
        case UserCommandStatus.Undefined:
        default:
            return "Неизвестно";
        case UserCommandStatus.InWork:
            return "Выполняется";
        case UserCommandStatus.Sended:
            return "Отправлено на устройство";
        case UserCommandStatus.Ok:
            return "Выполнено успешно";
        case UserCommandStatus.Error:
            return "Ошибка";
    }
}
interface IHardwareCommandTableRow {
    model: number;
    modelName: string;
    serialNumber: string;
    login: string;
    firmwareVersion?: string;
    commandStatus?: UserCommandStatus;
    commandState?: string;
    additionalInfo?: string;
    openCommandInfo?: string;
}
interface IDeviceFirmware {
    ProtocolType: number;
    Firmwares: string[];
    // Модель, которой принадлежат прошивки (структура хранения Firmware\{ModelId})
    ModelId: number;
}
interface IUserCommandStatusesByDevice {
    Statuses: Record<string, PerformUserCommandResponse>;
}
interface ILoginDevices {
    Login: string;
    Devices: string[];
}
interface IReprogGroup {
    model: number;
    modelName: string;
    devices: IHardwareCommandTableRow[];
    firmwareOptions: IFirmwareOption[];
}
interface IFirmwareOption {
    label: string;
    value: string;
}
export interface IHardwareCommandsProps {
    tabIsActive: boolean;
}
const getRowStyle = (row: RowClassParams<IHardwareCommandTableRow>) => {
    const status = row.data?.commandStatus;
    const error = status == UserCommandStatus.Error;
    const success = status == UserCommandStatus.Ok;
    const inWork = status == UserCommandStatus.InWork;

    if (!error && !success && !inWork)
        return;

    return {
        backgroundColor: success ? 'var(--more-hover-bg)' : inWork ? "var(--banned)" : error ? "var(--blocked)" : "var(--bg)",
        background: success ? 'var(--more-hover-bg)' :  inWork ? "var(--banned)" : error ? "var(--blocked)" : "var(--bg)",
        color: success || error || inWork ? "var(--text-dark)" : 'var(--text)',
        stroke: success || error || inWork ? "var(--text-dark)" : 'var(--text)',
    }
}
const columnStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column'
}

const HardwareCommands = (props: IHardwareCommandsProps) => {
    const { tabIsActive } = props;
    const [data, setData] = useState<IHardwareCommandTableRow[]>([]);
    const [firmwares, setFirmwares] = useState<IDeviceFirmware[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { deviceModels } = context;
    const [tableContainerHeight, setTableContainerHeight] = useState(88);

    const isMobile = useIsMobile();

    const tableRef = useRef<AgGridReact<IHardwareCommandTableRow>>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const buttonsPanelRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // dialog states for selecting deviceType before upload
    const [showDeviceTypeDialog, setShowDeviceTypeDialog] = useState(false);
    const [deviceTypeToUpload, setDeviceTypeToUpload] = useState<number | null>(null);

    const [showMobileStatusDialog, setShowMobileStatusDialog] = useState(false);
    const [selectedDeviceStatus, setSelectedDeviceStatus] = useState<IHardwareCommandTableRow | null>(null);

    // dialog states for selecting firmware per device model before reprogram
    const [showReprogDialog, setShowReprogDialog] = useState(false);
    const [reprogGroups, setReprogGroups] = useState<IReprogGroup[]>([]);
    const [reprogSelection, setReprogSelection] = useState<Record<number, string>>({});

    const modelOptions = useMemo(() => {
        const set = new Set<string>();
        data.forEach(d => {
            if (d.modelName) set.add(d.modelName);
        });
        return Array.from(set);
    }, [data]);

    const handleMobileOpenDevice = (row: CustomCellRendererProps<IHardwareCommandTableRow>) => {
        if (row.data) {
            const device = data.find(dev => dev.model == row.data?.model && dev.serialNumber == row.data.serialNumber);
            if (device) {
                setSelectedDeviceStatus(device);
                setShowMobileStatusDialog(true);
            }

        }
    }

    const columns = useMemo<ColDef<IHardwareCommandTableRow>[]>(() => [
        {
            headerName: "№",
            maxWidth: 50,
            editable: false,
            hide: isMobile,
            valueGetter: (props) => {
                return props.node!.rowIndex! + 1;
            }
        },
        {
            field: 'modelName',
            headerName: "Модель",
            floatingFilterComponentParams: { options: modelOptions },
            width: 125,
            minWidth: 125,
            editable: false,
            ...SelectFilter
        },
        {
            field: 'serialNumber',
            headerName: "с.н",
            editable: false,
            width: 75,
            maxWidth: 75,
            ...agTextColumnFilterProps(),
        },
        {
            field: 'firmwareVersion',
            headerName: "Версия прошивки",
            editable: false,
            hide: isMobile
        },
        {
            field: 'commandStatus',
            headerName: "Статус команды",
            editable: false,
            hide: isMobile,
            cellRenderer: (row: CustomCellRendererProps<IHardwareCommandTableRow>) => {
                return getUserCommandStatusDescription(row.value);
            }
        },
        {
            field: 'commandState',
            headerName: "Состояние команды",
            editable: false,
            hide: isMobile
        },
        {
            field: 'additionalInfo',
            headerName: "Дополнительно",
            editable: false,
            hide: isMobile
        },
        {
            field: 'openCommandInfo',
            headerName: "Статус",
            editable: false,
            width:75,
            hide: !isMobile,
            cellRenderer: (row: CustomCellRendererProps<IHardwareCommandTableRow>) => {
                return (
                    <Button onClick={() => handleMobileOpenDevice(row)} style={{ lineHeight: '1rem' }} size='sm' variant="custom">Открыть</Button>
                )
            }
        },
    ], [modelOptions]);

    const getFirmwares = () => {
        centerClient.getFirmwares()
            .then((res) => {
                const data = res.data;
                if (data)
                    setFirmwares(data.Items as unknown as IDeviceFirmware[]);
            })
            .catch(() => void 0);
    }

    const getDevices = () => {
        setIsLoading(true);
        centerClient.getAllDevices()
            .then((res) => {
                const data = res.data;
                if (!data)
                    return;
                const newData: IHardwareCommandTableRow[] = [];
                Object.entries(data).forEach(([_, accounts]) => {
                    Object.entries(accounts).forEach(([account, devices]) => {
                        devices.forEach(device => {
                            if (device.DeviceModelName && device.Serial)
                                newData.push({
                                    model: device.Model,
                                    modelName: device.DeviceModelName,
                                    serialNumber: device.Serial,
                                    login: account
                                })
                        });
                    })
                })
                setData(newData);
            })
            .catch(() => void 0)
            .finally(() => setIsLoading(false));
    }
    const loadUserCommandStatuses = () => {
        centerClient.getUserCommandsStatus()
            .then((res) => {
                const data = res.data as unknown as IUserCommandStatusesByDevice | undefined;
                if (!data || Object.entries(data.Statuses).length == 0)
                    return;
                const statuses = new Map(Object.entries(data.Statuses));
                tableRef.current?.api.forEachNodeAfterFilterAndSort((row) => {
                    if (!row.data)
                        return;

                    const deviceId = buildDeviceId(row.data.model, row.data.serialNumber);
                    if (statuses.has(deviceId)) {
                        const deviceStatus = statuses.get(deviceId);
                        if (!deviceStatus)
                            return;

                        row.setDataValue('commandStatus', deviceStatus.Status);
                        row.setDataValue('commandState', deviceStatus.DisplayDescription?.toString() ?? '');
                        row.setDataValue('additionalInfo', deviceStatus.ResultDescription?.toString() ?? '');
                        row.setDataValue('firmwareVersion', deviceStatus.Result ?? '');
                    }
                })
            })
            .catch(() => void 0);
    }
    useEffect(() => {
        getFirmwares();
        getDevices();
    }, []);

    useEffect((): (() => void) => {
        if (data.length == 0 || !tabIsActive)
            return () => void 0;
        sizeToFit();

        const timer = setIntervalAsync(async () => {
            await loadUserCommandStatuses();
        }, 2000);

        return async () =>
            await clearIntervalAsync(timer);
    }, [data, tabIsActive]);

    const onFilterChanged = useCallback(() => {
        if (tableRef.current) {
            // Deselect all rows when the filter changes
            tableRef.current.api.deselectAll();
        }
    }, []);

    const getTableHeight = () => {
        if (!buttonsPanelRef?.current)
            return;
        return buttonsPanelRef.current.getBoundingClientRect().height;
    }

    const sizeToFit = useCallback(() => {
        setTableContainerHeight(getTableHeight() ?? 88);
        // AG Grid #29: не вызываем sizeColumnsToFit, если контейнер ещё не виден (нулевая ширина, скрытый таб)
        const el = tableContainerRef.current;
        if (!el || el.clientWidth === 0)
            return;
        setTimeout(() => {
            tableRef.current?.api?.sizeColumnsToFit({
                defaultMinWidth: 50,
            });
        }, 0)
    }, []);

    useLayoutEffect(() => {
        // Вызов при монтировании и при resize
        window.addEventListener("resize", sizeToFit);
        return () => {
            window.removeEventListener("resize", sizeToFit);
        };
    }, [sizeToFit]);

    const putUserCommand = (type: UserCommandType, devices?: IHardwareCommandTableRow[], additionalArgs = "") => {
        const selectedDevices = devices ?? tableRef?.current?.api.getSelectedRows();
        if (!selectedDevices || selectedDevices.length == 0)
            return toast.warn("Необходимо выбрать хотя бы один контроллер");

        const groupedDevices = Object.groupBy(selectedDevices, ({ login }) => login);
        const devicesByLogin = Object.entries(groupedDevices).map(([login, devices]) => {
            const loginDevices: ILoginDevices = {
                Login: login,
                Devices: devices?.map(device => buildDeviceId(device.model, device.serialNumber)) ?? []
            }
            return loginDevices;
        });
        const userCommandRequest: UserCommandBaseRequest = {
            DevicesByLogin: devicesByLogin as unknown as LoginDevicesDto[],
            Type: type,
            AdditionalArgs: additionalArgs
        }

        centerClient.putUserCommand(userCommandRequest)
            .then(() => tableRef?.current?.api.deselectAll())
            .catch(() => void 0);
    }

    const openReprogDialog = () => {
        const selectedDevices = tableRef?.current?.api.getSelectedRows();
        if (!selectedDevices || selectedDevices.length == 0)
            return toast.warn("Необходимо выбрать хотя бы один контроллер");

        // прошивки приходят группами по модели (ModelId); подбираем набор строго под модель устройства
        const grouped = Object.groupBy(selectedDevices, ({ model }) => model);
        const groups: IReprogGroup[] = Object.entries(grouped).map(([model, devices]) => {
            const modelNum = Number(model);
            const fwGroup = firmwares?.find(f => f.ModelId === modelNum);
            return {
                model: modelNum,
                modelName: devices![0].modelName,
                devices: devices!,
                firmwareOptions: fwGroup?.Firmwares?.map(firmware => { return { label: firmware, value: firmware }; }) ?? []
            };
        });

        setReprogGroups(groups);
        setReprogSelection({});
        setShowReprogDialog(true);
    }

    const handleReprogSelect = (model: number, value: string) => {
        setReprogSelection(prev => ({ ...prev, [model]: value }));
    }

    const confirmReprog = () => {
        for (const group of reprogGroups) {
            if (group.firmwareOptions.length == 0)
                return toast.warn(`Для модели "${group.modelName}" нет доступных прошивок.`);
            if (!reprogSelection[group.model])
                return toast.warn(`Необходимо выбрать прошивку для модели "${group.modelName}".`);
        }
        reprogGroups.forEach(group =>
            putUserCommand(UserCommandType.ReprogFirmware, group.devices, '<FirmwareName>' + reprogSelection[group.model] + '</FirmwareName>'));
        setShowReprogDialog(false);
    }

    const addFirmware = () => {
        // Перед открытием выбора файла — выбираем deviceType
        if (!deviceModels || deviceModels.length === 0) {
            toast.warn("Список типов устройств пуст. Невозможно загрузить прошивку.");
            return;
        }

        //if (deviceModels.length === 1) {
        //    // если только один тип — выбираем автоматически
        //    setDeviceTypeToUpload(deviceModels[0].ModelId);
        //    // Открываем диалог выбора файла
        //    if (!fileInputRef.current) return;
        //    fileInputRef.current.value = '';
        //    fileInputRef.current.click();
        //} else
        {
            // показываем диалог выбора типа устройства
            setDeviceTypeToUpload(null);
            setShowDeviceTypeDialog(true);
        }
    }

    const onFirmwareSelected = async (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0)
            return;
        const file = files[0];

        if (!file.name.toLowerCase().endsWith('.cod')) {
            toast.error("Неверный формат файла. Ожидается файл с расширением .cod");
            e.target.value = '';
            return;
        }

        if (deviceTypeToUpload == null) {
            toast.error("Тип устройства не выбран. Повторите попытку.");
            e.target.value = '';
            return;
        }

        const form = new FormData();
        form.append('file', file);
        form.append('deviceType', deviceTypeToUpload.toString());

        try {
            await axiosWithAuth.post('api/Center/UploadFirmware', form).then((res) => {
                const successResult = res.data;
                if (successResult && successResult.success) {
                    toast.success("Прошивка успешно загружена");
                    // Обновим список прошивок
                    getFirmwares();
                } else {
                    const errText = successResult && successResult.responseText ? ` ${successResult.responseText}` : '';
                    toast.error("Не удалось загрузить прошивку." + errText);
                }
            })
                .catch((axiosErr) => {
                    throw axiosErr;
                });
            
        } catch (ex) {
            console.error(ex);
            toast.error("Не удалось загрузить прошивку. Внутренняя ошибка.");
        } finally {
            e.target.value = '';
            // сбрасываем выбранный тип после загрузки/ошибки
            setDeviceTypeToUpload(null);
        }
    }

    const confirmDeviceTypeAndOpenFile = () => {
        if (deviceTypeToUpload == null) {
            toast.warn("Необходимо выбрать тип устройства.");
            return;
        }
        setShowDeviceTypeDialog(false);
        // открыть диалог выбора файла
        if (!fileInputRef.current) return;
        fileInputRef.current.value = '';
        // небольшой таймаут, чтобы закрытие диалога отработало и фокус перешёл
        setTimeout(() => fileInputRef.current!.click(), 0);
    }

    const MobileCommandStatusDialog = ({ row }: { row: IHardwareCommandTableRow | null }) => {
        if (!row)
            return;

        return (
            <Modal show={showMobileStatusDialog} >
                <Modal.Header closeButton onHide={() => setShowMobileStatusDialog(false)} >
                    <Modal.Title>Статус команды</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col>
                            <Form.Group style={ columnStyle } >
                                <label style={{ fontWeight: 600 }} >Версия прошивки</label>
                                <label>{row.firmwareVersion}</label>
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group style={columnStyle}>
                                <label style={{ fontWeight: 600 }}>Статус</label>
                                {getUserCommandStatusDescription(row.commandStatus)}
                            </Form.Group>
                        </Col> 
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group style={columnStyle}>
                                <label style={{ fontWeight: 600 }}>Состояние</label>
                                {row.commandState}
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group style={columnStyle}>
                                <label style={{ fontWeight: 600 }}>Дополнительно</label>
                                {row.additionalInfo}
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowMobileStatusDialog(false); }}>Закрыть</Button>
                </Modal.Footer>
            </Modal>
        )
    }

    return (
        <div style={{ height: '100%' }}>
            {/* скрытый input для выбора файла прошивки */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".cod"
                style={{ display: 'none' }}
                onChange={onFirmwareSelected}
            />
            <Modal show={showDeviceTypeDialog} >
                <Modal.Header>
                    <Modal.Title>Выберите тип контроллера</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <InputPicker
                        data={deviceModels}
                        valueKey='ModelId'
                        labelKey='ModelName'
                        onSelect={setDeviceTypeToUpload}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowDeviceTypeDialog(false); setDeviceTypeToUpload(null); }}>Отмена</Button>
                    <Button variant="custom" onClick={confirmDeviceTypeAndOpenFile}>Выбрать</Button>
                </Modal.Footer>
            </Modal>
            <Modal show={showReprogDialog}>
                <Modal.Header>
                    <Modal.Title>Выберите прошивку</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {reprogGroups.map(group => (
                        <Form.Group key={group.model} style={{ ...columnStyle, marginBottom: '1rem' }}>
                            <label style={{ fontWeight: 600 }}>
                                {group.modelName} ({group.devices.length} шт.)
                            </label>
                            {group.firmwareOptions.length == 0
                                ? <label>Нет доступных прошивок</label>
                                : <InputPicker
                                    style={{ width: '100%' }}
                                    cleanable={false}
                                    placement='bottomStart'
                                    data={group.firmwareOptions}
                                    value={reprogSelection[group.model]}
                                    onSelect={(value: string) => handleReprogSelect(group.model, value)}
                                />}
                        </Form.Group>
                    ))}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowReprogDialog(false)}>Отмена</Button>
                    <Button variant="custom" onClick={confirmReprog}>Перепрошить</Button>
                </Modal.Footer>
            </Modal>
            <div ref={buttonsPanelRef} style={{ width: '100%', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <ButtonGroup style={{ flexWrap: 'wrap', gap: '0.35rem' }}>
                    <Button variant="custom" onClick={() => putUserCommand(UserCommandType.FirmwareVersion)}><b>Получить версию прошивки</b></Button>
                    <Button variant="custom" onClick={() => putUserCommand(UserCommandType.RestartDevice)}><b>Перезапустить устройство</b></Button>
                    <Button variant="custom" onClick={() => putUserCommand(UserCommandType.ResetFlash)}><b>Сброс флеш</b></Button>
                    <Button variant="custom" onClick={openReprogDialog}><b>Перепрошить устройство</b></Button>
                    <Button variant="custom" onClick={addFirmware}><b>Загрузить прошивку</b></Button>
                </ButtonGroup>
            </div>
            <div ref={tableContainerRef} style={{ height: `calc(100% - ${tableContainerHeight}px)` }}>
                <BlickAgGrid
                    showRestoreColumnsButton={true}
                    loading={isLoading}
                    rowData={data}
                    ref={tableRef}
                    onFilterChanged={onFilterChanged}
                    //theme={myAgReactTheme}
                    localeText={AG_GRID_LOCALE_RU}
                    columnDefs={columns}
                    onRowDataUpdated={sizeToFit}
                    selectionColumnDef={{ maxWidth:50 }}
                    rowSelection={{
                        mode: 'multiRow',
                        checkboxes: true
                    }}
                    getRowStyle={getRowStyle }
                    onModelUpdated={(e) => { e.api.refreshCells() }}
                />
            </div>
            <MobileCommandStatusDialog row={selectedDeviceStatus} />
        </div>
    )
};

export default HardwareCommands;
